import React, { useRef } from 'react'
import * as PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { debounce } from 'lodash'
import { useTranslation } from 'react-i18next'

import LogResult from './LogResult'

const BodyContainer = ({
  isLoading,
  actionLogs,
  onEditLog,
  staffPersons,
  building,
  loadMoreLogs,
}) => {
  const { t } = useTranslation(['action-log'])
  const containerRef = useRef(null)

  const actualDate = building
    ? moment(new Date()).tz(building.timezone, true).format('MM/DD/YYYY')
    : moment(new Date()).format('MM/DD/YYYY')

  const wrapTimezone = (date, displayToUser = false) => {
    return building
      ? moment(date)
          .tz(building.timezone)
          .format(displayToUser ? 'dddd, MMMM Do YYYY' : 'MM/DD/YYYY')
      : moment(date).format(displayToUser ? 'dddd, MMMM Do YYYY' : 'MM/DD/YYYY')
  }

  const isPrevious = (log, i) =>
    new Date(wrapTimezone(log.date)) < new Date(actualDate) &&
    (actionLogs[i - 1] ? wrapTimezone(actionLogs[i - 1].date) === actualDate : true)
  const isToday = (log, i) =>
    wrapTimezone(log.date) === actualDate &&
    (actionLogs[i - 1] ? wrapTimezone(actionLogs[i - 1].date) !== actualDate : true)
  const isUpcoming = (log, i) =>
    new Date(wrapTimezone(log.date)) > new Date(actualDate) &&
    (actionLogs[i - 1] ? wrapTimezone(actionLogs[i - 1].date) === actualDate : true)

  const loadMore = () => {
    if (isLoading) return
    if (containerRef.current) {
      const { offsetHeight, scrollTop, scrollHeight } = containerRef.current
      if (offsetHeight + Math.ceil(scrollTop) > scrollHeight - scrollHeight / 3) {
        loadMoreLogs()
      }
    }
  }

  return (
    <div
      className={`ctp-action-log-flex-container__body ${isLoading ? 'is-loading' : ''}`}
      ref={containerRef}
      onScroll={debounce(loadMore, 500)}
    >
      {actionLogs && actionLogs.length > 0 ? (
        actionLogs.map((log, i) => {
          if (isToday(log, i)) {
            return (
              <React.Fragment key={`${i}-fragment`}>
                <div className="tab-header">{t('action-log:bodyContainer.todayHeader')}</div>
                <div className="tab-date">{wrapTimezone(log.date, true)}</div>
                <LogResult
                  building={building}
                  onEditLog={onEditLog}
                  staffPersons={staffPersons}
                  data={log}
                  key={i}
                />
              </React.Fragment>
            )
          } else if (isUpcoming(log, i)) {
            return (
              <React.Fragment key={`${i}-fragment`}>
                <div key={`${i}-tab-header`} className="tab-header">
                  {t('action-log:bodyContainer.upcomingHeader')}
                </div>
                {actionLogs[i - 1] &&
                wrapTimezone(actionLogs[i - 1].date) !== wrapTimezone(log.date) ? (
                  <div className="tab-date">{wrapTimezone(log.date, true)}</div>
                ) : (
                  <div className="tab-date">{wrapTimezone(log.date, true)}</div>
                )}
                <LogResult
                  building={building}
                  onEditLog={onEditLog}
                  staffPersons={staffPersons}
                  data={log}
                  key={i}
                />
              </React.Fragment>
            )
          } else {
            return (
              <React.Fragment key={`${i}-fragment`}>
                {isPrevious(log, i) ? (
                  <div className="tab-header">{t('action-log:bodyContainer.previousHeader')}</div>
                ) : null}
                {actionLogs[i - 1] &&
                wrapTimezone(actionLogs[i - 1].date) !== wrapTimezone(log.date) ? (
                  <div className="tab-date">{wrapTimezone(log.date, true)}</div>
                ) : i === 0 ? (
                  <div className="tab-date">{wrapTimezone(log.date, true)}</div>
                ) : null}
                <LogResult
                  building={building}
                  onEditLog={onEditLog}
                  staffPersons={staffPersons}
                  data={log}
                  key={i}
                />
              </React.Fragment>
            )
          }
        })
      ) : (
        <div className="no-logs">{t('action-log:bodyContainer.noResultsMessage')}</div>
      )}
    </div>
  )
}

BodyContainer.propTypes = {
  isLoading: PropTypes.bool,
  actionLogs: PropTypes.arrayOf(PropTypes.object),
  onEditLog: PropTypes.func,
  staffPersons: PropTypes.array,
  building: PropTypes.object,
  loadMoreLogs: PropTypes.func,
}

export default BodyContainer
